import React, { useState, useEffect, useMemo, useCallback } from "react";
import { MathJax } from "better-react-mathjax";
import { MarkdownContent } from "./MarkdownContent";
import "../assets/styles/LessonPanels.css";

/**  LessonPanels
 *    difficulty: "easy" | "medium" | "hard"  (defaults to "medium")
 */
const LessonPanels = ({
  lessonBlock,
  difficulty = "medium",
  isExpanded,
  setIsExpanded,
  isMobile,
  activeTab: externalActiveTab,
  setActiveTab: setExternalActiveTab,
  noToggleButton = false,
}) => {
  /* ---------- tab handling ---------- */
  const activeTab = externalActiveTab || "lesson";

  /* ---------- welcome hint ---------- */
  const [showHint, setShowHint] = useState(true);
  useEffect(() => {
    if (!showHint) return;
    const t = setTimeout(() => setShowHint(false), 8_000);
    return () => clearTimeout(t);
  }, [showHint]);

  /* ---------- helpers ---------- */
  // Memoize diffContent to prevent recalculation on every render
  const diffContent = useMemo(
    () => lessonBlock?.lessonPlansByLevel?.[difficulty] ?? undefined,
    [lessonBlock, difficulty]
  );

  /* ---------- Section component (small helper) ---------- */
  const Section = useCallback(
    ({ title, body, hint }) => {
      return (
        <div className="lesson-section">
          <h3>{title}</h3>
          {body}
          {hint && (
            <div className="lesson-hint">
              📘 Here's your lesson plan — feel free to explore before we begin.
            </div>
          )}
        </div>
      );
    },
    []
  );

  const memoizedContent = useMemo(() => {
    /* Fallback helpers receive diffContent ---------- */
    const listOrMessage = (arr, Component = "ul") =>
      arr?.length ? (
        <Component className="lesson-list">
          {arr.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </Component>
      ) : (
        <p>Nothing yet.</p>
      );

    /* ---------- lesson‑plan section renderers ---------- */
    const renderOutcomes = () =>
      lessonBlock?.outcomesByLevel?.[difficulty]?.length ? (
        <ul className="lesson-list">
          {lessonBlock.outcomesByLevel[difficulty].map((x, i) => (
            <li key={i}>
              <span className="outcome-highlight">{x}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p>Nothing yet.</p>
      );

    const renderSteps = () => listOrMessage(diffContent?.steps, "ol");

    const renderPractice = () =>
      listOrMessage(
        diffContent?.checkUnderstanding?.map((q) => (
          <MathJax key={q} inline>
            {q}
          </MathJax>
        )),
        "ol"
      );

    /* ---------- study‑sheet section renderers ---------- */
    // const renderKeyIdeas = () => listOrMessage(diffContent?.keyIdeas);

    const renderConceptBoxes = () =>
      lessonBlock?.visualStudySheet?.conceptBoxes?.length ? (
        lessonBlock.visualStudySheet.conceptBoxes.map((c, i) => (
          <div className="concept-card" key={i}>
            <h4>{c.title}</h4>
            <MarkdownContent content={c.content} />
          </div>
        ))
      ) : (
        <p>No main ideas yet.</p>
      );

    const renderExamples = () =>
      lessonBlock?.visualStudySheet?.stepDiagrams?.length ? (
        lessonBlock.visualStudySheet.stepDiagrams.map((d, i) => (
          <div className="study-example" key={i}>
            <h4>{d.title}</h4>
            {d.steps.map((s, j) => (
              <div className="example-step" key={j}>
                <MarkdownContent content={s} />
              </div>
            ))}
          </div>
        ))
      ) : (
        <p>No worked examples yet.</p>
      );

    const renderCommonMistakes = () =>
      lessonBlock?.visualStudySheet?.commonMistakes?.length ? (
        lessonBlock.visualStudySheet.commonMistakes.map((m, i) => (
          <div className="common-mistake-card" key={i}>
            <h4>{m.title}</h4>
            <MarkdownContent content={m.content} />
          </div>
        ))
      ) : (
        <p>No common mistakes noted.</p>
      );

    /* ---------- intro / review ---------- */
    const intro =
      diffContent?.intro ??
      lessonBlock?.title ??
      `${lessonBlock?.chapter ?? ""} – ${lessonBlock?.unit ?? ""}`;
    const review = diffContent?.review ?? " ";

    /* Create components for the two tabs */
    const LessonPlan = () => (
      <>
        <Section title="Topic Overview" body={<MarkdownContent content={intro} />} hint={showHint} />
        <Section title="Lesson Outcomes" body={renderOutcomes()} />
        <Section title="Lesson Plan" body={renderSteps()} />
        <Section title="Review" body={<MarkdownContent content={review} />} />
        <Section title="Practice Questions" body={renderPractice()} />
      </>
    );

    const StudySheet = () => (
      <>
        {/* <Section title="Summary" body={renderKeyIdeas()} /> */}
        <Section title="Main Ideas" body={renderConceptBoxes()} />
        <Section title="Worked Example" body={renderExamples()} />
        <Section title="Common Mistakes" body={renderCommonMistakes()} />
      </>
    );

    return { LessonPlan, StudySheet };
  }, [lessonBlock, difficulty, showHint, diffContent]);

  /* ---------- render ---------- */
  /* Instead of using the tabs in the component, we'll let the parent handle tab switching */
  return (
    <div className={`lesson-panels-container ${isExpanded ? "expanded" : "collapsed"} ${noToggleButton ? 'sidebar-mode' : ''}`}>
      {!noToggleButton && (
        <div className="toggle-panel-btn" onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? "›" : "📘"}
        </div>
      )}
      <div className={`lesson-panel-content ${noToggleButton ? 'full-width' : ''}`}>
        <div className={`lesson-panel ${activeTab === "lesson" ? "active" : ""}`}>
          <memoizedContent.LessonPlan />
        </div>
        <div className={`lesson-panel ${activeTab === "study" ? "active" : ""}`}>
          <memoizedContent.StudySheet />
        </div>
      </div>
    </div>
  );
};

// Use React.memo to prevent re-renders if props don't change
export default React.memo(LessonPanels, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.lessonBlock === nextProps.lessonBlock &&
    prevProps.difficulty === nextProps.difficulty &&
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.activeTab === nextProps.activeTab &&
    prevProps.isMobile === nextProps.isMobile
  );
});